/**
 * Universal Search API - Searches across aircraft, clients, and spaces
 *
 * Runs 3 parallel Supabase queries with ILIKE matching.
 * Smart input detection: N+digit → aircraft first, T- → spaces first.
 */

import { supabase } from '@/lib/supabase'

// ============================================
// Result types
// ============================================

export interface AircraftSearchResult {
  allocationId: string
  tailNumber: string
  typeCode: string
  manufacturer: string | null
  model: string | null
  ownerName: string | null
  contractType: 'lease' | 'transient' | null
  areaName: string | null
  unitCode: string | null
  position: [number, number, number]
}

export interface ClientSearchResult {
  id: string
  displayName: string
  companyName: string | null
  clientType: string
  aircraftCount: number
}

export interface SpaceSearchResult {
  id: string
  type: 'area' | 'unit'
  name: string
  code: string
  parentName: string | null
}

export interface SearchResults {
  aircraft: AircraftSearchResult[]
  clients: ClientSearchResult[]
  spaces: SpaceSearchResult[]
}

// ============================================
// Search function
// ============================================

export async function searchRamp(
  query: string,
  organizationId: string,
  locationId: string
): Promise<SearchResults> {
  const q = query.trim()
  if (q.length < 2) return { aircraft: [], clients: [], spaces: [] }

  const pattern = `%${q}%`

  console.log('[Search] Query:', q, 'Pattern:', pattern, 'Location:', locationId, 'Account:', organizationId)

  // Step 1: Find aircraft IDs matching the tail number pattern, scoped to this location
  // (PostgREST doesn't reliably support ilike on embedded resources)
  const aircraftIdsResult = await supabase
    .from('aircraft')
    .select('id')
    .eq('organization_id', organizationId)
    .ilike('tail_number', pattern)
    .is('deleted_at', null)
    .or(`home_location_id.eq.${locationId},current_location_id.eq.${locationId}`)
    .limit(20)

  console.log('[Search] Step 1 - Aircraft IDs:', aircraftIdsResult.data, 'Error:', aircraftIdsResult.error)

  const matchingAircraftIds = (aircraftIdsResult.data || []).map((r: any) => r.id)

  console.log('[Search] Step 2 - Matching IDs:', matchingAircraftIds)

  // Run remaining queries in parallel (including allocation lookup if we found aircraft)
  const [aircraftResult, clientsResult, spacesResult] = await Promise.all([
    // 1. Aircraft search — fetch allocations for matching aircraft IDs
    matchingAircraftIds.length > 0
      ? supabase
          .from('allocations')
          .select(`
            id,
            position_x,
            position_y,
            position_z,
            lease_contract_id,
            transient_stay_id,
            aircraft!inner (
              tail_number,
              manufacturer,
              model,
              owner_name,
              aircraft_types ( type_code )
            ),
            areas ( name ),
            units ( unit_code )
          `)
          .eq('location_id', locationId)
          .is('deleted_at', null)
          .in('aircraft_id', matchingAircraftIds)
          .limit(8)
      : Promise.resolve({ data: [], error: null }),

    // 2. Client search
    supabase
      .from('clients')
      .select('id, first_name, last_name, company_name, client_type')
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .or(`company_name.ilike.${pattern},first_name.ilike.${pattern},last_name.ilike.${pattern}`)
      .limit(5),

    // 3. Space search (areas + units)
    supabase
      .from('areas')
      .select('id, name, area_code')
      .eq('location_id', locationId)
      .is('deleted_at', null)
      .or(`name.ilike.${pattern},area_code.ilike.${pattern}`)
      .limit(5),
  ])

  console.log('[Search] Results - Aircraft:', aircraftResult.data?.length, 'Error:', aircraftResult.error)
  console.log('[Search] Results - Clients:', clientsResult.data?.length, 'Error:', clientsResult.error)
  console.log('[Search] Results - Spaces:', spacesResult.data?.length, 'Error:', spacesResult.error)

  // Transform aircraft results
  const aircraft: AircraftSearchResult[] = (aircraftResult.data || []).map((row: any) => ({
    allocationId: row.id,
    tailNumber: row.aircraft?.tail_number || '',
    typeCode: row.aircraft?.aircraft_types?.type_code || '',
    manufacturer: row.aircraft?.manufacturer || null,
    model: row.aircraft?.model || null,
    ownerName: row.aircraft?.owner_name || null,
    contractType: row.lease_contract_id ? 'lease' : row.transient_stay_id ? 'transient' : null,
    areaName: row.areas?.name || null,
    unitCode: row.units?.unit_code || null,
    position: [
      Number(row.position_x) || 0,
      Number(row.position_y) || 0,
      Number(row.position_z) || 0,
    ] as [number, number, number],
  }))

  // Transform client results
  const clients: ClientSearchResult[] = (clientsResult.data || []).map((row: any) => ({
    id: row.id,
    displayName: row.company_name || [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Unknown',
    companyName: row.company_name,
    clientType: row.client_type,
    aircraftCount: 0, // TODO: could add a count query but keeping it simple for now
  }))

  // Transform space results
  const spaces: SpaceSearchResult[] = (spacesResult.data || []).map((row: any) => ({
    id: row.id,
    type: 'area' as const,
    name: row.name,
    code: row.area_code,
    parentName: null,
  }))

  return { aircraft, clients, spaces }
}
